import type { SessionExercise } from '../types';
import type { SessionTimer } from '../hooks/useSessionTimer';

interface SetupPhaseProps {
  exercise: SessionExercise;
  roundNumber: number;
  totalRounds: number;
  currentSide: 'left' | 'right' | null;
  timer: SessionTimer;
}

export function SetupPhase({
  exercise,
  roundNumber,
  totalRounds,
  currentSide,
  timer,
}: SetupPhaseProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-6 p-6 min-h-[60vh]">
      <p
        className="text-meta font-medium uppercase tracking-wider text-gray-500 dark:text-textDark-muted"
        data-testid="setup-phase-label"
      >
        Get Ready
      </p>

      <h2
        className="text-heading-xl text-gray-900 dark:text-textDark-primary text-center"
        data-testid="setup-exercise-name"
      >
        {exercise.exerciseName}
      </h2>

      <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-textDark-muted">
        <span data-testid="setup-round-indicator">
          Round {roundNumber} of {totalRounds}
        </span>
        {currentSide && (
          <>
            <span className="text-gray-300 dark:text-textDark-muted">·</span>
            <span
              className="rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-semibold text-brand-800 dark:bg-brand-900 dark:text-brand-200 capitalize"
              data-testid="setup-side-badge"
            >
              {currentSide}
            </span>
          </>
        )}
      </div>

      <span
        className="text-8xl font-mono font-bold tabular-nums text-brand-600 dark:text-brand-400"
        data-testid="setup-countdown"
      >
        {Math.ceil(timer.timeRemaining)}
      </span>

      {/* Countdown progress bar */}
      <div className="h-1.5 w-full max-w-lg overflow-hidden rounded-full bg-gray-200 dark:bg-dark-border">
        <div
          className="h-full rounded-full bg-brand-600 dark:bg-brand-400"
          style={{ width: `${(1 - timer.progress) * 100}%` }}
          data-testid="setup-countdown-bar"
        />
      </div>

      {/* Exercise preview */}
      <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500 dark:text-textDark-muted">
            {exercise.exerciseMode === 'RepBased'
              ? `${exercise.targetReps} reps`
              : `${exercise.targetDurationSeconds}s`}
          </span>
          {exercise.suggestedWeightKg != null && (
            <span className="text-gray-700 dark:text-textDark-secondary font-medium">
              {exercise.suggestedWeightKg} kg
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
